import type { Answers, FormContext, FormResponse, Upload } from '../../../shared/src/application-forms/types';
import { eligibilityApi } from '../../../shared/src/eligibility/eligibilityApi';
import { institutionPortfolioApi } from "../../../shared/src/components/institution-portfolio/institutionPortfolioApi";
import { agencyDocumentsApi } from '../../../shared/src/components/agency-documents/agencyDocumentsApi';
import { visaApi } from '../../../shared/src/components/visa-support/visaSupportApi';
import axios from "axios";
import {
  AgentCommission,
  AgentStats,
  AgentStudentApplication,
  AgentSupportContacts,
} from "@/types/user";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export interface AgentCourse {
  offeringId: number;
  universityId: number;
  programId: number;
  institutionName: string;
  programmeName: string;
  level: string;
  city: string;
  country: string;
  startDate: string | null;
  intakeDates: string | null;
  applicationDeadline: string | null;
  description: string | null;
  duration: string | null;
  canApply: boolean;
  availability: string;
}
export interface CourseSearchParams { search?: string; universityId?: number; level?: string; page?: number; limit?: number }

export const agentApi = {
  getApplicationForm: async (universityId: number, signal?: AbortSignal): Promise<FormContext> =>
    (await api.get(`/agent-application-forms/context/${universityId}`, { signal })).data.data,
  getApplicationFormResponse: async (id: number, signal?: AbortSignal): Promise<FormResponse | null> =>
    (await api.get(`/agent-application-forms/agent/applications/${id}/response`, { signal })).data.data,
  saveConfiguredApplication: async (data: { universityId: number; offeringId: number; applicationId?: number; response: { revisionId: number; draftKey: string; intake: string; answers: Answers } }): Promise<{ id: number; reference: string }> =>
    (await api.put('/agent-application-forms/draft', data)).data.data,
  uploadApplicationFormFile: async (data: FormData): Promise<Upload> =>
    (await api.post('/agent-application-forms/uploads', data, { headers: { 'Content-Type': 'multipart/form-data' } })).data.data,
  downloadApplicationFormFile: async (id: number, fileId: string): Promise<Blob> =>
    (await api.get(`/agent-application-forms/agent/applications/${id}/files/${fileId}`, { responseType: 'blob' })).data,

  getCourses: async (params: CourseSearchParams = {}, signal?: AbortSignal): Promise<{ items: AgentCourse[]; total: number; page: number; limit: number }> => {
    const response = await api.get('/agents/courses', { params, signal });
    return response.data.data;
  },
  getCourseOptions: async (signal?: AbortSignal): Promise<{ institutions: { id: number; name: string }[]; levels: string[] }> => {
    const response = await api.get('/agents/courses/options', { signal });
    return response.data.data;
  },
  getCourse: async (offeringId: number, signal?: AbortSignal): Promise<AgentCourse> => {
    const response = await api.get(`/agents/courses/${offeringId}`, { signal });
    return response.data.data;
  },
  // Dashboard stats
  getStats: async (): Promise<AgentStats> => {
    const response = await api.get("/agents/stats");
    return response.data.data;
  },

  getSupportContacts: async (signal?: AbortSignal): Promise<AgentSupportContacts> => {
    const response = await api.get('/agents/support-contacts', { signal });
    return response.data.data;
  },

  // Applications management
  getApplications: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    group?: "pending" | "approved";
    universityId?: number;
    universityProgramId?: number;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    includeSubAccounts?: boolean;
  }, signal?: AbortSignal): Promise<{
    applications: AgentStudentApplication[];
    total: number;
    page: number;
    limit: number;
    pages: number;
    stats?: any;
  }> => {
    const response = await api.get("/agents/applications", {
      signal,
      params: {
        ...params,
        includeSubAccounts: params?.includeSubAccounts ? "true" : "false",
      },
    });
    return response.data.data;
  },

  getApplication: async (
    applicationIdOrReference: number | string,
  ): Promise<AgentStudentApplication> => {
    const response = await api.get(
      `/agents/applications/${applicationIdOrReference}`,
    );
    return response.data.data;
  },

  createApplication: async (data: any): Promise<AgentStudentApplication> => {
    const response = await api.post("/agents/applications", data);
    return response.data.data;
  },

  updateApplication: async (
    applicationId: number,
    data: any,
  ): Promise<AgentStudentApplication> => {
    const response = await api.put(
      `/agents/applications/${applicationId}`,
      data,
    );
    return response.data.data;
  },

  submitApplication: async (
    applicationId: number,
    saveAsProfile?: boolean,
    profileId?: number,
  ): Promise<AgentStudentApplication> => {
    const response = await api.post(
      `/agents/applications/${applicationId}/submit`,
      {
        saveAsProfile,
        profileId,
      },
    );
    return response.data.data.application;
  },

  submitResponse: async (
    applicationId: number | string,
    response: string,
  ): Promise<AgentStudentApplication> => {
    const apiResponse = await api.post(
      `/agents/applications/${applicationId}/response`,
      { response },
    );
    return apiResponse.data.data;
  },

  addNote: async (
    applicationId: number | string,
    note: string,
  ): Promise<AgentStudentApplication> => {
    const apiResponse = await api.post(`/agents/applications/${applicationId}/notes`, {
      note,
    });
    return apiResponse.data.data;
  },

  deleteApplication: async (applicationId: number): Promise<void> => {
    await api.delete(`/agents/applications/${applicationId}`);
  },

  transferApplication: async (
    applicationId: number,
  ): Promise<AgentStudentApplication> => {
    const response = await api.put(
      `/agents/applications/${applicationId}/transfer`,
    );
    return response.data.data;
  },

  reassignApplication: async (
    applicationId: number,
    subAccountId: number,
  ): Promise<AgentStudentApplication> => {
    const response = await api.put(
      `/agents/applications/${applicationId}/reassign`,
      { subAccountId },
    );
    return response.data.data;
  },

  // Commission tracking
  getCommissions: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    commissions: AgentCommission[];
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await api.get("/agents/commissions", { params });
    return response.data.data;
  },

  getCommission: async (
    commissionId: string | number,
  ): Promise<AgentCommission> => {
    const response = await api.get(`/agents/commissions/${commissionId}`);
    return response.data.data;
  },

  requestPayout: async (
    commissionId: string | number,
  ): Promise<AgentCommission> => {
    const response = await api.post(
      `/agents/commissions/${commissionId}/request-payout`,
    );
    return response.data.data;
  },

  // Profile management
  // Notification preferences
  getNotificationPreferences: async (): Promise<{
    notifyOnSubAccountApplicationUpdates: boolean;
    isSubAccount: boolean;
  }> => {
    const response = await api.get("/agents/notification-preferences");
    return response.data.data;
  },

  updateNotificationPreferences: async (data: {
    notifyOnSubAccountApplicationUpdates: boolean;
  }): Promise<{
    notifyOnSubAccountApplicationUpdates: boolean;
  }> => {
    const response = await api.put("/agents/notification-preferences", data);
    return response.data.data;
  },

  updateProfile: async (data: {
    firstName?: string;
    lastName?: string;
    agencyName?: string;
    licenseNumber?: string;
    phone?: string;
  }): Promise<any> => {
    const response = await api.put("/agents/profile", data);
    return response.data.data;
  },

  // Password change
  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> => {
    await api.post("/agents/change-password", data);
  },

  // Profile picture
  uploadProfilePicture: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", "profile-picture");

    const response = await axios.post(
      `${API_BASE_URL}/agents/profile-picture`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );
    return response.data.data.url;
  },

  // Partnerships
  getPartnerships: async (params?: { status?: string }): Promise<any[]> => {
    const response = await api.get("/agents/partnerships", { params });
    return response.data.data;
  },

  requestPartnership: async (universityId: number): Promise<any> => {
    const response = await api.post("/agents/partnerships/request", {
      universityId,
    });
    return response.data.data;
  },

  getInstitutions: async (): Promise<any[]> => {
    const response = await api.get("/agents/institutions");
    return response.data.data;
  },

  getProfilePicture: async (): Promise<string | null> => {
    try {
      const response = await api.get("/agents/profile-picture");
      // API returns { success: false, error: 'Profile picture not found' } when missing
      if (response.data?.success === false) {
        return null;
      }
      return response.data?.data?.url || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Payout account details
  updatePayoutAccountDetails: async (data: {
    bankName?: string;
    accountHolderName?: string;
    accountNumber?: string;
    routingNumber?: string;
    swiftCode?: string;
    accountType?: "CHECKING" | "SAVINGS";
    bankAddress?: string;
    currency?: string;
  }): Promise<any> => {
    const response = await api.put("/agents/payout-account", data);
    return response.data.data;
  },

  // Reports
  getEarningsReport: async (params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: "day" | "week" | "month";
  }): Promise<any> => {
    const response = await api.get("/agents/reports/earnings", { params });
    return response.data.data;
  },

  // Universities and Programs
  getApprovedInstitutions: async (): Promise<any[]> => {
    const response = await api.get("/agents/institutions");
    return response.data.data;
  },

  getUniversities: async (params?: {
    search?: string;
    country?: string;
    page?: number;
    limit?: number;
  }): Promise<any> => {
    const response = await api.get("/universities", { params });
    return response.data.data;
  },

  getUniversityPrograms: async (
    universityId: number,
    params?: { page?: number; limit?: number; level?: string; search?: string },
  ): Promise<any> => {
    // Default high limit for dropdowns (Programme Strand, etc.) so full list is returned
    const response = await api.get(`/universities/${universityId}/programs`, {
      params: { limit: 500, ...params },
    });
    return response.data.data;
  },

  getUniversityById: async (universityId: number): Promise<any> => {
    const response = await api.get(`/universities/${universityId}`);
    return response.data.data;
  },

  // Document upload
  uploadDocument: async (
    file: File,
    category: string,
    applicationId?: number,
    documentRequestId?: string,
    operationId?: string,
  ): Promise<any> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", category);
    if (documentRequestId) formData.append("documentRequestId", documentRequestId);
    if (operationId) formData.append("operationId", operationId);

    // Use agent-specific endpoint if applicationId is provided
    const endpoint = applicationId
      ? `/agents/applications/${applicationId}/documents`
      : `/documents/upload`;

    const response = await axios.post(`${API_BASE_URL}${endpoint}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    return response.data;
  },

  // Get application documents
  getApplicationDocuments: async (applicationId: number): Promise<any[]> => {
    const response = await api.get(
      `/agents/applications/${applicationId}/documents`,
    );
    return response.data.data || [];
  },

  // Download application document
  downloadApplicationDocument: async (
    applicationId: number,
    documentId: number,
  ): Promise<Blob> => {
    const response = await axios.get(
      `${API_BASE_URL}/agents/applications/${applicationId}/documents/${documentId}/download`,
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob",
      },
    );
    return response.data;
  },

  // Download application PDF
  downloadApplicationPDF: async (
    applicationId: number | string,
  ): Promise<Blob> => {
    const response = await axios.get(
      `${API_BASE_URL}/agents/applications/${applicationId}/pdf`,
      {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "blob",
      },
    );
    return response.data;
  },

  // Get application activity logs
  getApplicationActivity: async (
    applicationId: number,
    params?: {
      page?: number;
      limit?: number;
    },
  ): Promise<any> => {
    const response = await api.get(
      `/agents/applications/${applicationId}/activity`,
      { params },
    );
    return response.data.data;
  },

  // Notifications
  getNotifications: async (params?: {
    page?: number;
    limit?: number;
    isRead?: boolean;
  }): Promise<{
    notifications: Array<{
      id: number;
      userId: number;
      type: string;
      title: string;
      message: string;
      data?: Record<string, any>;
      isRead: boolean;
      createdAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await api.get("/agents/notifications", { params });
    return response.data.data;
  },

  getUnreadNotificationCount: async (): Promise<number> => {
    const response = await api.get("/agents/notifications/unread-count");
    return response.data.data.count;
  },

  markNotificationAsRead: async (notificationId: number): Promise<void> => {
    await api.patch(`/agents/notifications/${notificationId}/read`);
  },

  markAllNotificationsAsRead: async (): Promise<void> => {
    await api.patch("/agents/notifications/read-all");
  },

  // Sub-accounts management
  getSubAccounts: async (): Promise<any[]> => {
    const response = await api.get("/agents/sub-accounts");
    return response.data.data;
  },

  getSubAccount: async (id: number): Promise<any> => {
    const response = await api.get(`/agents/sub-accounts/${id}`);
    return response.data.data;
  },

  createSubAccount: async (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    agencyName?: string;
    canAccessCommissions?: boolean;
    canAccessPartnerships?: boolean;
  }): Promise<any> => {
    const response = await api.post("/agents/sub-accounts", data);
    return response.data.data;
  },

  updateSubAccount: async (
    id: number,
    data: {
      email?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      password?: string;
      agencyName?: string;
      isActive?: boolean;
      canAccessCommissions?: boolean;
      canAccessPartnerships?: boolean;
    },
  ): Promise<any> => {
    const response = await api.put(`/agents/sub-accounts/${id}`, data);
    return response.data.data;
  },

  deleteSubAccount: async (id: number): Promise<void> => {
    await api.delete(`/agents/sub-accounts/${id}`);
  },

  // Student Profiles
  getStudentProfiles: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    email?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }, signal?: AbortSignal): Promise<{
    profiles: any[];
    total: number;
    page: number;
    limit: number;
    pages: number;
  }> => {
    const response = await api.get("/agents/student-profiles", { params, signal });
    return response.data.data;
  },

  getStudentProfile: async (profileId: number): Promise<any> => {
    const response = await api.get(`/agents/student-profiles/${profileId}`);
    return response.data.data;
  },

  createStudentProfile: async (data: {
    applicationId?: number;
    profileData?: any;
  }): Promise<any> => {
    const response = await api.post("/agents/student-profiles", data);
    return response.data.data;
  },

  updateStudentProfile: async (profileId: number, data: any): Promise<any> => {
    const response = await api.put(
      `/agents/student-profiles/${profileId}`,
      data,
    );
    return response.data.data;
  },

  deleteStudentProfile: async (profileId: number): Promise<void> => {
    await api.delete(`/agents/student-profiles/${profileId}`);
  },
};

export const agentVisaApi = visaApi(api, (id,file)=>agentApi.downloadApplicationDocument(id,file.id), (id,request,file,operation)=>agentApi.uploadDocument(file,request.category,id,request.id,operation));

export const agentAgencyDocumentsApi = agencyDocumentsApi(api);

export const agentInstitutionPortfolioApi = institutionPortfolioApi(api);

export const agentEligibilityApi = eligibilityApi(api);
